import {
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Title3,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  BuildingMultipleRegular,
  CheckmarkCircleRegular,
  DatabaseRegular,
  PeopleRegular,
  PulseRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import type { PlatformStats, TenantSummary } from '@worklogix/shared';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError, usePlatformApi } from '../api';
import { DataGrid } from '../components/DataGrid';
import type { ResourceColumn } from '../components/DataGrid';

import { Tile } from './Tile';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
});

/** How many tenants to surface in the dashboard's recent list. */
const RECENT_LIMIT = 5;

const TENANT_COLUMNS: ReadonlyArray<ResourceColumn<TenantSummary>> = [
  { columnId: 'name', header: 'Name', renderCell: (t) => t.name },
  { columnId: 'slug', header: 'Slug', renderCell: (t) => t.slug },
];

/**
 * Platform-admin landing page: KPI tiles fed by `GET /platform/stats` plus a
 * recent-tenants resource grid from `GET /platform/tenants`. Row click drills
 * into the tenant detail route.
 */
export function Dashboard(): JSX.Element {
  const styles = useStyles();
  const api = usePlatformApi();
  const navigate = useNavigate();

  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const [statsResult, tenantsResult] = await Promise.all([api.getStats(), api.listTenants()]);
        if (!cancelled) {
          setStats(statsResult);
          setTenants(tenantsResult);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof ApiError ? err.message : 'Unable to load platform data.';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [api]);

  if (loading) {
    return (
      <div className={styles.center}>
        <Spinner label="Loading dashboard…" />
      </div>
    );
  }

  // Soft-warn compliance KPI: how many tenants have more users than their plan
  // allows. Informational only — 0 is the neutral/good state (success glyph),
  // >0 surfaces an amber warning, NEVER an error/blocked treatment.
  const tenantsOverLimit = stats?.tenantsOverLimit ?? 0;
  const hasOverLimit = tenantsOverLimit > 0;

  return (
    <div className={styles.root}>
      <Title3 as="h1">Platform overview</Title3>

      {error !== null && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.tiles}>
        <Tile label="Tenants" value={stats?.tenants ?? '—'} icon={<BuildingMultipleRegular />} />
        <Tile label="Users" value={stats?.users ?? '—'} icon={<PeopleRegular />} />
        <Tile
          label="API health"
          value={stats?.apiStatus ?? '—'}
          icon={<PulseRegular />}
          caption="Control-plane API"
        />
        <Tile
          label="Database"
          value={stats?.dbStatus ?? '—'}
          icon={<DatabaseRegular />}
          caption="Postgres connectivity"
        />
        <Tile
          label="Tenants over limit"
          value={stats ? tenantsOverLimit : '—'}
          icon={
            hasOverLimit ? (
              <WarningRegular style={{ color: tokens.colorStatusWarningForeground1 }} />
            ) : (
              <CheckmarkCircleRegular style={{ color: tokens.colorStatusSuccessForeground1 }} />
            )
          }
          caption={hasOverLimit ? 'Over their plan limit' : 'All within plan limits'}
        />
      </div>

      <div className={styles.section}>
        <Text weight="semibold">Recent tenants</Text>
        <DataGrid<TenantSummary>
          columns={TENANT_COLUMNS}
          rows={tenants.slice(0, RECENT_LIMIT)}
          getRowId={(t) => t.id}
          onRowClick={(t) => {
            void navigate(`/tenants/${t.id}`);
          }}
          emptyMessage="No tenants provisioned yet."
        />
      </div>
    </div>
  );
}
